/**
 * MinuteOfSilence — a gentle 60-second remembrance timer.
 * No religious assumption. No loud animation. Non-intrusive, tasteful.
 */
import React, { useState, useEffect, useRef } from 'react';
import { Flame, Heart } from 'lucide-react';
import { Button } from '../ui/button';

export function MinuteOfSilence({ personName, onComplete }) {
  const [state, setState] = useState('idle'); // idle | running | done
  const [seconds, setSeconds] = useState(60);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (state !== 'running') return;
    intervalRef.current = setInterval(() => {
      setSeconds((s) => {
        if (s <= 1) {
          clearInterval(intervalRef.current);
          setState('done');
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current);
  }, [state]);

  const handleStart = () => { setState('running'); setSeconds(60); };
  const handleStop = () => { clearInterval(intervalRef.current); setState('done'); };

  const progress = ((60 - seconds) / 60) * 100;
  const name = personName ? personName : 'their memory';

  return (
    <div className="rounded-2xl border border-[hsl(38_14%_86%)]/60 bg-[#0f0f1a] px-6 py-8 text-center" data-testid="minute-of-silence">
      {/* Candle icon */}
      <div className="mb-4 flex justify-center">
        <div
          className="flex h-14 w-14 items-center justify-center rounded-full"
          style={{ background: 'rgba(201,169,97,0.1)', border: '1px solid rgba(201,169,97,0.3)' }}
        >
          <Flame
            className="h-7 w-7"
            style={{
              color: '#c9a961',
              filter: state === 'running' ? 'drop-shadow(0 0 8px #c9a96160)' : 'none',
              transition: 'filter 0.5s ease',
            }}
            aria-hidden
          />
        </div>
      </div>

      {state === 'idle' && (
        <>
          <h3 className="mb-1 font-serif text-lg font-light text-white">Begin a quiet minute</h3>
          <p className="mb-5 text-sm text-white/50">
            A moment to remember {name}.
            {'\n'}No noise. No rush.
          </p>
          <Button
            type="button"
            className="rounded-full bg-[#c9a961]/20 text-[#c9a961] border border-[#c9a961]/30 hover:bg-[#c9a961]/30"
            onClick={handleStart}
          >
            Begin
          </Button>
        </>
      )}

      {state === 'running' && (
        <>
          <p className="mb-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#c9a961]/60">
            Quiet moment
          </p>
          <p
            className="mb-1 font-serif text-5xl font-light text-white tabular-nums"
            aria-live="polite"
            aria-label={`${seconds} seconds remaining`}
          >
            {String(seconds).padStart(2, '0')}
          </p>
          <p className="mb-5 text-xs text-white/40">seconds remaining</p>

          {/* Progress bar */}
          <div className="mx-auto mb-5 h-0.5 w-40 overflow-hidden rounded-full bg-white/10">
            <div
              className="h-full rounded-full transition-all duration-1000"
              style={{ width: `${progress}%`, background: '#c9a961' }}
            />
          </div>

          <button
            type="button"
            className="text-xs text-white/30 hover:text-white/60 transition-colors"
            onClick={handleStop}
          >
            Skip
          </button>
        </>
      )}

      {state === 'done' && (
        <>
          <Heart className="mx-auto mb-3 h-6 w-6 text-[#c9a961]" aria-hidden />
          <p className="mb-1 font-serif text-base text-white">Thank you for this moment.</p>
          <p className="mb-5 text-sm text-white/50">
            Would you like to add a memory or share a tribute?
          </p>
          <div className="flex justify-center gap-2">
            <Button
              type="button"
              size="sm"
              className="rounded-full bg-[#c9a961]/20 text-[#c9a961] border border-[#c9a961]/30 hover:bg-[#c9a961]/30"
              onClick={() => onComplete?.('memory')}
            >
              Add a memory
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="rounded-full text-white/40 hover:text-white/70"
              onClick={() => onComplete?.('close')}
            >
              Close
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default MinuteOfSilence;
